import {useMemo} from 'react';
import {Map} from 'immutable';
import type {UseBoundStore, StoreApi} from 'zustand';
import type { ItemBankStoreInstance} from '../FitbItemBank/store';
import {createUseItemBankStoreInstance} from '../FitbItemBank/store';
import type { OrderingStoreInstance} from '../Ordering/store';
import {createOrderingStoreInstance} from '../Ordering/store';
import type {CoreQuestionStore} from '../QuestionType.types';
/**
 * This file consists of hooks and functions for utilizing the new zustand question stores into
 * the current question flow. The first portion regarding the storeRegistry, is a recreation of the pattern
 * utilized in the current mandark framework.js. Because zustand stores are not objects like the legacy stores
 * There is one workaround we made to make draft guesses compatible with the current way questions are used
 *
 * Previously stores were accessed directly via question.getStore(). This approach does not work with zustand and hooks
 * as those are more opinionated and have different restrictions on how state is managed.
 *
 * The work around I wrote allows us to have multiple instances of zustand stores that can be accessed, as opposed to
 * looping through questions and mapping the values from stores.
 *
 * As of now `useQuestionStore` is used for constructing props for the legacy assignment flows to work
 *
 * For example, an assignment gets all draft ids by looping through question sets and getting the values from question.getStore().
 * Hooks cannot be used in a class component, and not in the way the store objects were being utilized. The solution
 * was to create a draftId prop, that loops through all the question store hook instances, gets the ids and creates a prop
 * This does not violate hook rules, because this should only be done once the total question sets are fetched which allows for
 * the order and number of hooks to remain the same.
 *
 * Once the new question types are rendering correctly, we can move towards creating a new draft guess store, if the only reason
 * we have multiple instances of question stores is for draft info.
 */

// ------------ repeats what framework.js does for creating/storing stores ------------------

let storeRegistry = Map();

export const getRegisteredStores = () => {
  return storeRegistry;
};

export const getStoreByName = (name: string) => {
  return storeRegistry.getIn([name, 'store'], false);
};

const hasStoreInRegistry = (name) => storeRegistry.has(name);

export const questionTypes = ['item-bank', 'ordering'] as const; // see newStores in 'augmented/Question/QuestionModel.v3.js

export type QuestionTypes = (typeof questionTypes)[number];

export const isFitbItemBank = (qType: QuestionTypes): qType is 'item-bank' => {
  return qType === 'item-bank';
};

type ItemStore = ItemBankStoreInstance & UseBoundStore<StoreApi<CoreQuestionStore>>;

type OrderingStore = OrderingStoreInstance & UseBoundStore<StoreApi<CoreQuestionStore>>;

export type QuestionTypeMap = {
  // this is a temporary solution to get the types working,
  ['ordering']: OrderingStore;
  ['item-bank']: ItemStore;
};

export function setUpQuestionStore<T extends keyof QuestionTypeMap>(questionType: T, name: string) {
  // create new store instance if one does not exist
  if (!hasStoreInRegistry(name)) {
    if (isFitbItemBank(questionType)) {
      const storeInstance = createUseItemBankStoreInstance();
      storeRegistry = storeRegistry.set(
        name,
        Map({
          store: storeInstance
        })
      );
      return storeInstance as QuestionTypeMap[T];
    } else {
      const storeInstance = createOrderingStoreInstance(); // placeholder until ordering store is created
      storeRegistry = storeRegistry.set(
        name,
        Map({
          store: storeInstance
        })
      );
      return storeInstance as QuestionTypeMap[T];
    }
  }
  // otherwise return the existing store instance
  if (isFitbItemBank(questionType)) {
    return getStoreByName(name) as QuestionTypeMap[T];
  } else {
    return getStoreByName(name) as QuestionTypeMap[T];
  }
}

// ------------ End of framework.js replication  ------------------

/*
Functions for determining what type of store a given instances is. Mainly used for question render areas as
legacy stores and zustand stores have different ways of interacting with them.

example:

const storeName = isLegacyStore(store) ? activeQuestionStore.getName() : activeQuestion.getStoreName();

or

if(isLegacyStore(store)){
   callTargetedAction({...})
}

*/
const legacyStoreAttributes = ['getQuestionId', 'getQuestion', 'name', 'handlers'];

export const isLegacyStore = (store) => {
  return (
    typeof store === 'object' &&
    legacyStoreAttributes.every((attr) => Object.keys(store).includes(attr))
  );
};

export const hasZustand = (store) => {
  const containsZustand = store && !isLegacyStore(store);
  return containsZustand;
};

export const useQuestionStore = <T extends keyof QuestionTypeMap>(question) => {
  const useStoreHook = useMemo(
    () =>
      setUpQuestionStore<T>(
        question?.getType?.(),
        question?.getStoreName?.() || question?.getType?.()
      ),
    [question]
  );

  return useStoreHook;
};

export default useQuestionStore;
